import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from '@/lib/api/client';
import type { InfoCard } from './types';

/**
 * Info cards — the promotional panels the app and the web front show.
 *
 * Three endpoints, and `POST marketing/infocard` is both create and update:
 * an empty `id` inserts, a present one updates. The list arrives paged
 * (`OkPageData`), which is why the results are one level in.
 */

const KEY = ['info-cards'] as const;

interface PagedInfoCards {
  results: InfoCard[];
}

export function useInfoCards() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => get<PagedInfoCards>('marketing/infocards'),
    // Already ordered by start then end date server-side; kept as sent so the
    // list matches what the API considers the running order.
    select: (data) => data.results ?? [],
    staleTime: 60_000,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

/**
 * Create or update, decided by `id`.
 *
 * The whole card goes back every time — the endpoint replaces rather than
 * merges, so omitting a field clears it.
 */
export function useSaveInfoCard() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (card: InfoCard) => post<InfoCard>('marketing/infocard', card),
    onSuccess: invalidate,
  });
}

/**
 * Archive. The endpoint is a DELETE and the original labelled the button
 * "Archive"; the card is gone from the list either way, so the label follows
 * what happens rather than what the verb is.
 */
export function useDeleteInfoCard() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) => del<unknown>(`marketing/infocard/${id}`),
    onSuccess: invalidate,
  });
}
